import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useStreetFood } from '../data/street-food';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, MapPin } from 'lucide-react';
export function StreetFoodSection() {
  const { lang, isRTL } = useLanguage();
  const isAr = lang === 'ar';
  const { vendors, isLoading } = useStreetFood();
  if (isLoading || vendors.length === 0) return null;
  // Prefer vendors flagged for the homepage, but fall back to the first 3 so the section is never empty
  const featured = vendors.filter((v) => v.featuredOnHomepage);
  const showcaseVendors = (featured.length > 0 ? featured : vendors).slice(0, 3);
  if (showcaseVendors.length === 0) return null;
  return (
    <section className="my-16 md:my-24 bg-sand py-16 md:py-24 rounded-[3rem] px-4 md:px-12 mx-4 md:mx-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <motion.div
              initial={{
                opacity: 0,
                y: 10
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              viewport={{
                once: true
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta/10 text-terracotta text-sm font-bold mb-4">
              
              <Flame size={16} className="fill-terracotta" />
              <span>{isAr ? 'أسرار البلد' : 'Local Secrets'}</span>
            </motion.div>
            <motion.h2
              initial={{
                opacity: 0,
                y: 10
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              viewport={{
                once: true
              }}
              transition={{
                delay: 0.1
              }}
              className="font-serif text-4xl md:text-5xl text-sea-blue font-bold leading-tight">
              
              {isAr ? 'أكل الشارع السكندري' : 'Alexandrian Street Food'}
            </motion.h2>
            <motion.p
              initial={{
                opacity: 0,
                y: 10
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              viewport={{
                once: true
              }}
              transition={{
                delay: 0.2
              }}
              className="text-sea-blue/85 mt-4 text-lg">
              
              {isAr ?
              'مش مجرد مطاعم، دي حواديت. من عربيات الفول الخشب لمحلات الجزارة اللي بتتحول بالليل. اكتشف أسرار الأكل اللي ميعرفهاش غير ولاد البلد.' :
              'Not just restaurants—these are stories. From wooden carts to butchers that transform at night. Discover the culinary secrets only locals know.'}
            </motion.p>
          </div>
          <motion.div
            initial={{
              opacity: 0,
              x: isRTL ? -20 : 20
            }}
            whileInView={{
              opacity: 1,
              x: 0
            }}
            viewport={{
              once: true
            }}>
            
            <Link
              to="/street-food"
              className="flex items-center gap-2 text-terracotta font-medium hover:text-terracotta-hover transition-colors whitespace-nowrap bg-white px-6 py-3 rounded-full shadow-soft hover:shadow-float border border-sea-blue/5">
              
              {isAr ? 'اكتشف كل الحواديت' : 'Explore All Stories'}
              <ArrowRight size={18} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {showcaseVendors.map((vendor, index) =>
          <motion.div
            key={vendor.id}
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            viewport={{
              once: true
            }}
            transition={{
              delay: index * 0.1
            }}
            className="h-full">
            
              <Link
              to={`/street-food/${vendor.id}`}
              className="group block bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-float transition-all duration-300 border border-sea-blue/5 h-full flex flex-col">
              
                <div className="relative h-56 overflow-hidden shrink-0">
                  <img
                  src={vendor.images[0]}
                  alt={vendor.name[lang]}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                
                  <div className="absolute top-4 start-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-[10px] font-bold text-sea-blue uppercase tracking-wider shadow-sm">
                    {vendor.vendorType.replace('-', ' ')}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow bg-white">
                  <h3 className="font-serif text-2xl font-bold text-sea-blue mb-2">
                    {vendor.name[lang]}
                  </h3>
                  <p className="text-sea-blue/85 line-clamp-2 mb-6 flex-grow text-sm leading-relaxed">
                    {vendor.storyExcerpt[lang]}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-terracotta font-medium mt-auto pt-4 border-t border-sea-blue/5">
                    <MapPin size={16} className="shrink-0" />
                    <span className="truncate">
                      {vendor.neighborhood[lang]}
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </section>);

}